// "Send for client approval" (V0.4.11): a variation emailed to client finance. Like the claim send window — pick what
// goes in (variation details ticked by default, ready to proceed, support documents, comment register, responses,
// related emails), see the email exactly as it will go, tick "recipients checked", Send. SWI-only comments never go.
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button, Drawer, errText, useToast } from '../kit';
import { Recipients } from '../claim/SendWindow';
import type { FileRow } from '../ItemFiles';
import { C3Loader, C3Busy } from '../C3Loader';
import { OfflineNote, useConnection } from '../../lib/connection';

type Preview = {
  to: string[];
  cc: string[];
  defaults: string[];
  suggest: { email: string; name: string; org: string }[];
  subject: string;
  html: string;
  attachments: { filename: string; bytes: number }[];
  counts: { comments: number; responses: number; emails: number };
};
type Rtp = '' | 'yes' | 'hold' | 'no';
const FIELDS = [
  ['name', 'Variation name'],
  ['topic', 'Topic'],
  ['type', 'Type'],
  ['ref', 'Var ref'],
  ['building', 'Building'],
  ['contractor', 'Contractor'],
  ['directed', 'Directed to'],
  ['amounts', 'Amounts (VEP + VIP)'],
  ['submitted', 'Date submitted'],
  ['summary', 'SWI summary / notes'],
] as const;
type FieldKey = (typeof FIELDS)[number][0];
const kb = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const LIMIT = 25 * 1024 * 1024;

export function VariationSend({
  itemId,
  files,
  swiApproved,
  hasSummary,
  onClose,
  onSent,
}: {
  itemId: string;
  files: FileRow[];
  swiApproved: boolean;
  hasSummary: boolean;
  onClose: () => void;
  onSent: (o: { markSwiApproved: boolean }) => void;
}) {
  const toast = useToast();
  const conn = useConnection();
  const docs = files.filter((f) => f.is_current && f.kind === 'file' && !f.deleted_at);
  const [p, setP] = useState<Preview | null>(null);
  const [err, setErr] = useState('');
  const [to, setTo] = useState<string[] | null>(null);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [note, setNote] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'send' | null>('preview');
  const [f, setF] = useState<Record<FieldKey, boolean>>({ name: true, topic: true, type: true, ref: true, building: true, contractor: true, directed: true, amounts: true, submitted: false, summary: true });
  const [rtp, setRtp] = useState<Rtp>('yes');
  const [markSwi, setMarkSwi] = useState(true);
  const [extra, setExtra] = useState({ comments: false, responses: false, emails: false, link: true });
  const [pick, setPick] = useState<string[]>([]);
  const first = useRef(true);
  const include = { ...f, ...extra, rtp, files: pick };
  const size = docs.filter((d) => pick.includes(d.id)).reduce((s, d) => s + Number(d.size_bytes ?? 0), 0);

  async function load() {
    setBusy('preview');
    setErr('');
    try {
      const r = await api<Preview>('variation-send', { item_id: itemId, note, include, preview: true });
      setP(r);
      if (first.current) {
        setTo(r.to);
        setSubject(r.subject);
        first.current = false;
      }
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(null);
    }
  }
  const key = JSON.stringify(include) + note;
  useEffect(() => {
    const t = setTimeout(() => void load(), first.current ? 0 : 600);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setChecked(false), [to, cc]);

  async function send() {
    if (!to?.length) return toast({ tone: 'error', text: 'Add at least one recipient' });
    setBusy('send');
    try {
      const r = await api<{ to: string[] }>('variation-send', { item_id: itemId, note, include, to, cc, subject });
      toast({ tone: 'ok', text: `Sent to ${r.to.join(', ')}` });
      onSent({ markSwiApproved: rtp === 'yes' && !swiApproved && markSwi });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }

  const n = (to?.length ?? 0) + cc.length;
  const c = p?.counts;
  return (
    <Drawer open wide title="Send for client approval" onClose={onClose}>
      <div className="sw">
        <div className="sw-form">
          {err ? <div className="error-box">{err}</div> : null}
          <Recipients label="To" list={to ?? []} onChange={setTo} />
          {p?.suggest.some((sg) => !(to ?? []).includes(sg.email)) ? (
            <div className="sw-suggest">
              <span className="muted small">Finance contacts:</span>
              {p.suggest
                .filter((sg) => !(to ?? []).includes(sg.email))
                .map((sg) => (
                  <button key={sg.email} type="button" className="chip" onClick={() => setTo([...(to ?? []), sg.email])} title={sg.email}>
                    + {sg.name || sg.email}
                    {sg.org ? <span className="muted"> · {sg.org}</span> : null}
                  </button>
                ))}
            </div>
          ) : null}
          {p && !p.suggest.length ? <p className="muted small">No client finance contacts yet — type the addresses, or add contacts to the finance organisation under Organisations &amp; contacts.</p> : null}
          <Recipients label="Cc" list={cc} onChange={setCc} />
          <label className="field">
            <span className="field-label">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />
          </label>
          <label className="field">
            <span className="field-label">Note at the top of the email (optional)</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
          </label>

          <div className="sw-extras">
            <span className="sw-k">Ready to proceed</span>
            <div className="sw-rtp" role="radiogroup" aria-label="Ready to proceed">
              {(
                [
                  ['yes', '✓ Ready to proceed'],
                  ['hold', 'On hold'],
                  ['no', 'Not ready'],
                  ['', 'Don’t show'],
                ] as const
              ).map(([k, l]) => (
                <button key={k || 'none'} type="button" role="radio" aria-checked={rtp === k} className={`rtp rtp-${k || 'none'} ${rtp === k ? 'on' : ''}`} onClick={() => setRtp(k)}>
                  {l}
                </button>
              ))}
            </div>
            {rtp === 'yes' && !swiApproved ? (
              <label className="check small">
                <input type="checkbox" checked={markSwi} onChange={(e) => setMarkSwi(e.target.checked)} /> Also mark the variation SWI approved (ready to proceed) when sent
              </label>
            ) : null}
          </div>

          <div className="sw-extras">
            <span className="sw-k">Variation details</span>
            <div className="vs-grid">
              {FIELDS.map(([k, l]) => (
                <label key={k} className="check small">
                  <input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} /> {l}
                </label>
              ))}
            </div>
            {f.summary && !hasSummary ? <p className="warn-t small">No SWI summary yet — write it in "Summary / notes" on the variation; it is the justification the client reads.</p> : null}
          </div>

          <div className="sw-extras">
            <span className="sw-k">Support documents {pick.length ? `· ${pick.length} · ${kb(size)}` : ''}</span>
            {docs.length ? (
              <div className="vs-docs" role="group" aria-label="Support documents">
                {docs.map((d) => {
                  const on = pick.includes(d.id);
                  return (
                    <label key={d.id} className={`vs-doc ${on ? 'on' : ''}`}>
                      <input type="checkbox" checked={on} onChange={() => setPick(on ? pick.filter((x) => x !== d.id) : [...pick, d.id])} />
                      <span className="grow">{d.original_name}</span>
                      <span className="muted small">{kb(Number(d.size_bytes ?? 0))}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="muted small">No files on this variation yet.</p>
            )}
            {docs.length > 1 ? (
              <button type="button" className="linkbtn small" onClick={() => setPick(pick.length === docs.length ? [] : docs.map((d) => d.id))}>
                {pick.length === docs.length ? 'Untick all' : 'Tick all'}
              </button>
            ) : null}
            {size > LIMIT ? <p className="warn-t small">Over 25 MB — email will refuse it. Untick some, or rely on the PM C3 link.</p> : null}
          </div>

          <div className="sw-extras">
            <span className="sw-k">Also include</span>
            <label className="check small">
              <input type="checkbox" checked={extra.comments} onChange={(e) => setExtra({ ...extra, comments: e.target.checked })} /> Comment register{c ? ` (${c.comments} shared)` : ''}
            </label>
            <label className="check small">
              <input type="checkbox" checked={extra.responses} onChange={(e) => setExtra({ ...extra, responses: e.target.checked })} /> Responses &amp; revisions{c ? ` (${c.responses})` : ''}
            </label>
            <label className="check small">
              <input type="checkbox" checked={extra.emails} onChange={(e) => setExtra({ ...extra, emails: e.target.checked })} /> Related emails — list{c ? ` (${c.emails})` : ''}
            </label>
            <label className="check small">
              <input type="checkbox" checked={extra.link} onChange={(e) => setExtra({ ...extra, link: e.target.checked })} /> Link to this variation in PM C3
            </label>
            <span className="muted small">SWI-only comments and remarks are never sent.</span>
          </div>

          <label className="check sw-confirm">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} /> I have checked the recipients ({n})
          </label>
          <div className="resp-row">
            <span className="grow" />
            <Button onClick={onClose}>Cancel</Button>
            <OfflineNote />
            <Button kind="primary" disabled={!checked || !to?.length || Boolean(busy) || size > LIMIT || conn.status === 'offline'} onClick={() => void send()}>
              {busy === 'send' ? <C3Busy text="Sending…" accent /> : `Send to ${n} recipient${n === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
        <div className="sw-preview">
          <div className="sw-k">Preview {busy === 'preview' ? '· updating…' : ''}</div>
          {p ? (
            <>
              <div className="sw-meta">
                <b>{subject || p.subject}</b>
                <span className="muted small">
                  to {(to ?? []).join(', ') || '—'}
                  {cc.length ? ` · cc ${cc.join(', ')}` : ''}
                </span>
              </div>
              <iframe title="Email preview" className="sw-frame" sandbox="" srcDoc={p.html} />
              {p.attachments.map((a) => (
                <div key={a.filename} className="sw-att">
                  <span className="ext">{(/\.(\w+)$/.exec(a.filename)?.[1] ?? 'file').toUpperCase().slice(0, 4)}</span>
                  <span className="grow">{a.filename}</span>
                  <span className="muted small">{kb(a.bytes)}</span>
                </div>
              ))}
            </>
          ) : (
            busy ? <p className="muted c3-inline"><C3Loader size={32} /> Building the email…</p> : null
          )}
        </div>
      </div>
    </Drawer>
  );
}
